<template>
    <div class="mb-3">
        <div
            class="flex flex-col items-center gap-2 lg:grid lg:grid-cols-3 lg:gap-0"
        >
            <div>
                <h3
                    v-if="totalCalls > 1 && selected.length"
                    class="text-lg text-theme-base"
                >
                    Productos seleccionados
                    {{ selected.length }}
                </h3>
            </div>
            <h1 class="text-3xl capitalize text-center">
                Productos encontrados
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-end">
                <app-button
                    v-if="
                        $ability.hasPermission('product:Record') &&
                        !relevanceMode
                    "
                    color="success"
                    size="large"
                    @click="isCreationModalOpen = true"
                >
                    <span class="hidden md:inline">Nuevo producto</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap mt-5">
            <div v-if="!relevanceMode" class="w-full">
                <products-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    :default-filters="$data.$defaultFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center mt-2">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <advanced-options-button
                        v-if="!relevanceMode && advancedOptions.length"
                        :options="advancedOptions"
                        @option-click="onAdvancedOption"
                    />
                    <tooltip-button
                        v-show="!relevanceMode && selected.length"
                        icon="📕"
                        message="PDF de productos seleccionados"
                        @click="downloadPDFComponent(true)"
                    />
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('product:Publish')
                        "
                        icon="🟡"
                        message="Activa / Desactiva productos seleccionados"
                        @click="activeMassiveProductsComponent"
                    />

                    <tooltip-button
                        v-show="selected.length"
                        icon="👀"
                        message="Vista previa de productos seleccionados"
                        @click="openPreview"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <div v-if="relevanceMode" class="flex items-center gap-2">
                        <custom-checkbox v-model="reorderAll">
                            Afectar todo
                        </custom-checkbox>
                        <app-button color="success" @click="saveRelevance">
                            Guardar orden 💾
                        </app-button>
                        <app-button color="warn" @click="toggleRelevanceMode">
                            ⚖️ Salir
                        </app-button>
                    </div>

                    <list-settings-button
                        v-if="!relevanceMode"
                        :value="listSettings"
                        :sort-options="sortOptions"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="activeFields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                :selectable="!relevanceMode"
                :draggable="relevanceMode"
                :external-current-page="currentPage"
                :external-total-pages="pagesNumber"
                :row-actions="rowActions"
                select-mode="multi"
                responsive
                no-border-collapse
                hover
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
                @drag-drop="onDragDrop"
                @row-send-to-page="onRowSendToPage"
                @row-action="onRowAction"
            >
                <template #cell(image)="data">
                    <router-link :to="`/app/products/detail/${data.item.id}`">
                        <div class="w-12 h-12 flex items-center justify-center">
                            <img
                                v-img-loading
                                :src="
                                    (data.item.references &&
                                    data.item.references.length &&
                                    data.item.references[0].images &&
                                    data.item.references[0].images.length
                                        ? data.item.references[0].images[0].url
                                        : undefined) | image
                                "
                                alt="Product image"
                                class="w-12 h-12 object-cover rounded"
                                loading="lazy"
                            />
                        </div>
                    </router-link>
                </template>
                <template #cell(sku)="data">
                    <router-link
                        :to="`/app/products/detail/${data.item.id}`"
                        class="flex flex-col items-center font-mono rounded-full w-min-16 px-1 py-1 text-white"
                        :class="
                            data.item.active
                                ? 'bg-theme-success'
                                : 'bg-theme-disabled'
                        "
                    >
                        <span class="text-xs font-mono">
                            {{
                                (data.item.sku || data.item.id) | cut(16, false)
                            }}
                        </span>
                    </router-link>
                </template>
                <template #cell(slug)="data">
                    <small class="text-center">
                        {{ data.item.slug | cut(30) }}
                    </small>
                </template>
                <template #cell(name)="data">
                    <span class="text-center">
                        {{ data.item.name | cut(36) }}
                    </span>
                </template>
                <template #cell(summary)="data">
                    <small class="text-left break-all">
                        {{ data.item.summary | cut(30) }}
                    </small>
                </template>
                <template #cell(price)="data">
                    <a
                        :href="`/app/orders/product/${data.item.id}`"
                        target="_blank"
                        rel="noopener"
                        class="flex flex-col items-center"
                    >
                        <span class="text-center font-italic font-bold">
                            {{ data.item.priceOffer | money }}
                        </span>
                        <span
                            v-if="data.item.priceOffer < data.item.price"
                            class="text-center font-italic line-through"
                        >
                            &nbsp; {{ data.item.price | money }}
                        </span>
                    </a>
                </template>
                <template #cell(brandSlug)="data">
                    <a
                        :href="`/app/brands/detail/${data.item.brandSlug}`"
                        target="_blank"
                        rel="noopener"
                        class="flex flex-col items-center text-theme-base btn-a"
                    >
                        <small class="text-center">
                            {{ data.item.brandSlug | cut(40) }}
                        </small>
                    </a>
                </template>
                <template #cell(lineSlug)="data">
                    <a
                        :href="`/app/lines/detail/${data.item.lineSlug}`"
                        target="_blank"
                        rel="noopener"
                        class="flex flex-col items-center text-theme-base btn-a"
                    >
                        <small class="text-center">
                            {{ data.item.lineSlug | cut(40) }}
                        </small>
                    </a>
                </template>
                <template #cell(updatedAt)="data">
                    <small class="text-center">
                        {{ data.item.updatedAt | date }}
                    </small>
                </template>
            </custom-sortable-table>
        </div>

        <product-creation-modal v-model="isCreationModalOpen" />

        <app-dialog v-model="isSortModalOpen">
            <template #header>
                <h4 class="text-lg font-semibold text-theme-base">
                    Ordenar productos
                </h4>
            </template>
            <div class="flex flex-col gap-4 p-8 min-w-64">
                <products-sort-selector
                    v-model="sortConfig"
                    mode="buttons"
                    @change="
                        reloadItems();
                        isSortModalOpen = false;
                    "
                />
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenModalErrors">
            <template #header>
                <div
                    class="mx-4 mt-2 flex justify-center items-center flex-col lg:flex-row"
                >
                    <span class="text-lg"> Error en operación </span>
                </div>
            </template>
            <div>
                Errores encontrados
                <error-viewer
                    v-for="(errorItem, index) in errors"
                    :key="index"
                    :error="errorItem"
                    :base="errorsKeys"
                />
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenPreview" size="lg">
            <template #header>
                <div class="flex justify-between">
                    <div></div>
                    <div
                        class="mx-4 mt-2 flex justify-center items-center flex-col"
                    >
                        <h3 class="text-xl text-theme-primary font-bold">
                            Vista Previa de Productos Seleccionados
                        </h3>
                        <p class="text-sm mt-1">
                            {{ selected.length }} producto{{
                                selected.length !== 1 ? "s" : ""
                            }}
                            seleccionado{{ selected.length !== 1 ? "s" : "" }}
                        </p>
                    </div>
                    <app-button
                        :loading="isGeneratingPDF"
                        @click="downloadPDFComponent(true)"
                    >
                        📄 PDF
                    </app-button>
                </div>
            </template>

            <div class="px-4">
                <div class="overflow-y-auto" style="max-height: 75vh">
                    <div
                        v-for="product in selected"
                        :key="product.id"
                        class="mb-6 border rounded-lg p-4 shadow-sm"
                    >
                        <div class="flex items-start gap-4 mb-3 border-b pb-3">
                            <div class="flex-shrink-0">
                                <img
                                    v-if="
                                        product.references &&
                                        product.references.length &&
                                        product.references[0].images &&
                                        product.references[0].images.length
                                    "
                                    v-img-loading
                                    :src="
                                        product.references[0].images[0].url
                                            | image
                                    "
                                    :alt="product.references[0].images[0].alt"
                                    class="w-36 h-36 object-cover rounded-lg shadow"
                                    loading="lazy"
                                />
                                <div
                                    v-else
                                    class="w-36 h-36 rounded-lg flex items-center justify-center text-3xl"
                                >
                                    📦
                                </div>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center gap-2 mb-2">
                                    <span
                                        class="text-xs h-6 w-6 rounded-full ml-1 flex items-center justify-center"
                                        :class="
                                            product.active
                                                ? 'bg-theme-success text-white'
                                                : 'bg-theme-disabled'
                                        "
                                    >
                                        {{
                                            product.active ? product.weight : ""
                                        }}
                                    </span>
                                    <h4
                                        class="text-lg font-bold text-theme-primary"
                                    >
                                        {{ product.name }}
                                    </h4>
                                </div>
                                <div class="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span class="font-semibold">SKU:</span>
                                        {{ product.sku || product.id }}
                                    </div>
                                    <div>
                                        <span class="font-semibold">Slug:</span>
                                        {{ product.slug | cut(30) }}
                                    </div>
                                    <div>
                                        <span class="font-semibold">
                                            Marca:
                                        </span>
                                        {{ product.brandSlug }}
                                    </div>
                                    <div v-if="!$isMultitenant">
                                        <span class="font-semibold">
                                            Categoría:
                                        </span>
                                        {{ product.lineSlug }}
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <div
                                    class="text-2xl font-bold text-theme-primary"
                                >
                                    {{ product.priceOffer | money }}
                                </div>
                                <div
                                    v-if="product.priceOffer < product.price"
                                    class="text-sm line-through"
                                >
                                    {{ product.price | money }}
                                </div>
                            </div>
                        </div>

                        <div class="mb-3">
                            <p class="text-sm">
                                {{ product.summary }}
                            </p>
                        </div>

                        <div>
                            <h5
                                class="text-sm font-semibold mb-2 text-theme-base"
                            >
                                Variantes y Precios
                            </h5>
                            <div class="overflow-x-auto">
                                <table class="w-full text-sm border-collapse">
                                    <thead>
                                        <tr>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                Código
                                            </th>
                                            <th
                                                class="px-2 py-1 text-left border"
                                            >
                                                Variante
                                            </th>
                                            <th
                                                class="px-2 py-1 text-right border"
                                            >
                                                Precio de venta
                                            </th>
                                            <th
                                                class="px-2 py-1 text-right border"
                                            >
                                                Precio anterior
                                            </th>
                                            <th
                                                class="px-2 py-1 text-center border"
                                            >
                                                Stock
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="ref in product.references"
                                            :key="ref.id"
                                            class="hover:bg-theme-hover"
                                        >
                                            <td
                                                class="px-2 py-1 border text-xs"
                                            >
                                                {{ ref.barcode }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border font-medium"
                                            >
                                                <div class="flex flex-col">
                                                    <span class="capitalize">
                                                        {{
                                                            [
                                                                ref.size,
                                                                ref.color
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" / ")
                                                        }}
                                                    </span>
                                                    <small
                                                        v-if="
                                                            ref.customReferenceValue
                                                        "
                                                        class="text-xs capitalize"
                                                    >
                                                        {{
                                                            product.customReferenceTitle
                                                                ? `${product.customReferenceTitle}: `
                                                                : ""
                                                        }}{{
                                                            ref.customReferenceValue
                                                        }}
                                                    </small>
                                                </div>
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-right font-semibold"
                                                :class="{
                                                    'text-theme-success':
                                                        isOfferPrice(
                                                            ref.price,
                                                            ref.priceOffer
                                                        )
                                                }"
                                            >
                                                {{ ref.priceOffer | money }}
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-right"
                                            >
                                                <template
                                                    v-if="
                                                        isOfferPrice(
                                                            ref.price,
                                                            ref.priceOffer
                                                        )
                                                    "
                                                >
                                                    {{ ref.price | money }}
                                                </template>
                                                <template v-else>—</template>
                                            </td>
                                            <td
                                                class="px-2 py-1 border text-center font-medium"
                                                :class="{
                                                    'text-theme-danger':
                                                        ref.stock === 0,
                                                    'text-theme-success':
                                                        ref.stock > 0
                                                }"
                                            >
                                                {{ ref.stock }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import { REORDER_PAGE_SIZE } from "@/common/constants";
import { downloadFileMassiveProducts } from "@/common/lib/excel";
import {
    generateProductsListCompletePDF,
    generateProductsListPDF
} from "@/common/lib/pdf";
import { isOfferPrice } from "@/common/lib/price-offer";

import ProductCreationModal from "@/components/products/ProductCreationModal.vue";
import ProductsFilters from "@/components/products/ProductsFilters.vue";
import AdvancedOptionsButton from "@/components/utils/AdvancedOptionsButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import ErrorViewer from "@/components/utils/ErrorViewer.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "ProductsIndex",
    components: {
        AppDialog,
        ProductCreationModal,
        ProductsFilters,
        AdvancedOptionsButton,
        TooltipButton,
        ErrorViewer,
        CustomSortableTable
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        isCreationModalOpen: false,
        isSortModalOpen: false,
        query: {},
        itemsCurrent: [],
        $fields: [
            { key: "image", label: "Foto", sortable: false },
            { key: "sku", label: "Sku", sortable: false },
            { key: "name", label: "Nombre", sortable: true },
            { key: "slug", label: "Slug", sortable: true },
            { key: "price", label: "Precio de venta", sortable: true },
            { key: "brandSlug", label: "Marca", sortable: true },
            { key: "lineSlug", label: "Categoría", sortable: true },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        $enabledFilters: [
            "brand",
            "line",
            "keyword",
            "active",
            "barcode",
            "sku",
            "stock",
            "priceOffer"
        ],
        $defaultFilters: ["keyword"],
        $urlPaginationKey: "page",
        $urlSortKey: "sort",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "brandSlug", type: "string" },
            { key: "lineSlug", type: "string" },
            { key: "sku", type: "string" },
            { key: "barcode", type: "string" },
            { key: "active", type: "boolean" },
            { key: "stock", type: "range", subKeys: ["start", "end"] },
            { key: "priceOffer", type: "range", subKeys: ["start", "end"] }
        ],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        totalCalls: 0,
        selected: [],
        selectedProductsMap: new Map(),
        isOpenPreview: false,
        isLoadingDownload: false,
        isGeneratingPDF: false,
        errors: [],
        errorsKeys: ["id"],
        isOpenModalErrors: false,
        sortConfig: { orderBy: "updatedAt", orderType: "DESC" },
        relevanceMode: false,
        reorderAll: false,
        pageSize: 24
    }),
    computed: {
        ...mapState("products", ["products"]),
        ...mapGetters("control", ["backgroundColor"]),
        listPageSize() {
            return this.relevanceMode ? REORDER_PAGE_SIZE : this.pageSize;
        },
        rowActions() {
            if (this.selected.length > 1) {
                return [
                    {
                        key: "open",
                        icon: "🔗",
                        label: "Abrir en nueva pestaña"
                    },
                    { key: "report", icon: "📕", label: "Generar PDF" }
                ];
            }

            const actions = [
                { key: "open", icon: "🔗", label: "Abrir en nueva pestaña" },
                { key: "sales", icon: "📈", label: "Ventas por producto" },
                { key: "preview", icon: "👀", label: "Vista previa" },
                { key: "report", icon: "📕", label: "Generar PDF" }
            ];
            if (this.$ability.hasPermission("product:Create")) {
                actions.push({
                    key: "clone",
                    icon: "🗂️",
                    label: "Clonar producto"
                });
            }
            if (this.$ability.hasPermission("product:Publish")) {
                actions.push({
                    key: "toggle-active",
                    icon: "🟡",
                    label: "Activa / Desactiva"
                });
            }
            return actions;
        },
        advancedOptions() {
            const options = [];
            if (this.itemsCurrent.length > 0) {
                options.push({
                    key: "pdf",
                    icon: "📕",
                    label: "Descargar resultado PDF",
                    description:
                        "Genera un PDF con los productos de la página actual.",
                    group: "action"
                });
            }
            if (this.$ability.hasPermission("product:List")) {
                options.push({
                    key: "download",
                    icon: "⬇",
                    label: "Descargar todos los productos",
                    description:
                        "Exporta un Excel con todas las referencias. Aplica los filtros de publicado, stock y precio de la búsqueda actual.",
                    group: "action"
                });
            }
            if (
                this.$ability.hasPermission("product:Update") &&
                this.$enabledEcommerceOptions
            ) {
                options.push({
                    key: "edit-order",
                    icon: "⚖️",
                    label: "Editar secuencia",
                    description:
                        "Reordena la relevancia de los productos arrastrándolos en la tabla."
                });
            }
            return options;
        },
        activeFields() {
            if (this.relevanceMode) {
                return [
                    {
                        key: "weight",
                        label: "Peso",
                        sortable: false,
                        thClass: "w-14",
                        tdClass: "w-14"
                    },
                    {
                        key: "sku",
                        label: "Sku",
                        sortable: false,
                        thClass: "w-40",
                        tdClass: "w-40"
                    },
                    {
                        key: "name",
                        label: "Nombre",
                        sortable: false,
                        thClass: "w-96",
                        tdClass: "w-96"
                    },
                    {
                        key: "price",
                        label: "Precio de venta",
                        sortable: false,
                        thClass: "w-56",
                        tdClass: "w-56"
                    },
                    {
                        key: "updatedAt",
                        label: "📆 Actualización",
                        sortable: false,
                        thClass: "w-36",
                        tdClass: "w-36"
                    }
                ];
            }
            if (this.$isMultitenant) {
                return this.$data.$fields.filter(
                    (field) => field.key !== "lineSlug"
                );
            }
            return this.$data.$fields;
        },
        listSettings() {
            return { pageSize: this.pageSize, sortConfig: this.sortConfig };
        },
        sortOptions() {
            return [
                { label: "📆 Actualización reciente", value: "updatedAt:DESC" },
                { label: "📆 Actualización antigua", value: "updatedAt:ASC" },
                { label: "🗓️ Creación reciente", value: "createdAt:DESC" },
                { label: "🗓️ Creación antigua", value: "createdAt:ASC" },
                { label: "⚖️ Por peso / relevancia", value: "weight:ASC" },
                { label: "💰 Precio mayor", value: "priceOffer:DESC" },
                { label: "💰 Precio menor", value: "priceOffer:ASC" },
                { label: "🔤 Nombre A–Z", value: "name:ASC" },
                { label: "🔤 Nombre Z–A", value: "name:DESC" }
            ];
        }
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("product:List", "/app"))) return;
        this.listItems(true);
    },
    methods: {
        isOfferPrice,
        ...mapActions("products", [
            "listMassiveProducts",
            "listProductsPaginated",
            "publishMassiveProducts",
            "updateProductsWeights"
        ]),

        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listProductsPaginated({
                    ...this.query,
                    complete,
                    page: this.currentPage,
                    itemsByPage: this.listPageSize,
                    orderBy: this.sortConfig.orderBy,
                    orderType: this.sortConfig.orderType
                });
                this.itemsCurrent = [];
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$nextTick(() => this.joinSelected(items));
                this.totalCalls = this.totalCalls + 1;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize, sortConfig }) {
            this.pageSize = pageSize;
            if (sortConfig) this.sortConfig = sortConfig;
            this.reloadItems();
        },
        async activeMassiveProductsComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a alternal la publicación de ${
                    this.selected.length
                } producto${this.selected.length > 1 ? "s" : ""} ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#ee3f59",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const { errors, pass, productsUpdated } =
                    await this.publishMassiveProducts({
                        products: this.selected.map((item, index) => ({
                            item: { id: item.id },
                            index
                        }))
                    });

                if (!pass) {
                    this.isOpenModalErrors = true;
                    this.errors = errors;
                    return;
                }
                let itemsCurrent = [...this.itemsCurrent];
                productsUpdated.forEach((item) => {
                    const index = itemsCurrent.findIndex(
                        (item2) => item2.id == item.id
                    );
                    if (index !== -1) {
                        itemsCurrent[index] = {
                            ...itemsCurrent[index],
                            ...item
                        };
                    }
                });
                this.itemsCurrent = [...itemsCurrent];
                this.selected = [];
                this.$sound.play();
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.currentStep = 3;
            }
        },
        joinSelected(items) {
            const itemsMap = new Map();

            this.selected.forEach((item) => {
                itemsMap.set(item.id, item);
            });
            items.forEach((item) => {
                if (!itemsMap.has(item.id)) {
                    itemsMap.set(item.id, item);
                }
            });

            this.itemsCurrent = Array.from(itemsMap.values());
            this.$refs.selectableTable.selectedIndex = this.selected.map(
                (_, index) => index
            );
            this.$refs.selectableTable.selectedRows = this.selected;
        },
        onRowSelected(items) {
            const currentPageIds = new Set(this.itemsCurrent.map((p) => p.id));
            const itemsMap = new Map();
            this.selected.forEach((item) => {
                itemsMap.set(item.id, item);
            });
            for (const id of currentPageIds) {
                itemsMap.delete(id);
            }

            items.forEach((item) => {
                itemsMap.set(item.id, item);
            });

            this.selected = Array.from(itemsMap.values());
        },
        openPreview() {
            this.isOpenPreview = true;
        },
        async goToPage(page) {
            this.currentPage = page;
            await this.listItems(true);
        },
        async onRowSendToPage({ item, page }) {
            if (page === this.currentPage) return;
            const targetWeight = page * this.listPageSize - 1;
            this.itemsCurrent = this.itemsCurrent.filter(
                (p) => p.id !== item.id
            );
            this.$loading.show();
            try {
                await this.updateProductsWeights({
                    products: [{ id: item.id, weight: targetWeight }]
                });
                this.$sound.play();
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.itemsCurrent = [...this.itemsCurrent, item];
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        toggleRelevanceMode() {
            this.relevanceMode = !this.relevanceMode;
            if (this.relevanceMode) {
                this.sortConfig = { orderBy: "weight", orderType: "ASC" };
                this.query = { active: true };
            } else {
                this.sortConfig = { orderBy: "updatedAt", orderType: "DESC" };
                this.query = {};
                this.reorderAll = false;
            }
            this.currentPage = 1;
            this.listItems(true);
        },
        onDragDrop(reorderedItems) {
            const offset = (this.currentPage - 1) * this.listPageSize;
            this.itemsCurrent = reorderedItems.map((p, index) => ({
                ...p,
                weight: offset + index + 1
            }));
        },
        async saveRelevance() {
            if (this.reorderAll) {
                const result = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "¿Guardar orden afectando todos los productos?",
                    text: "Tienes activa la opción 'Afectar todo'. El orden se aplicará a todos los productos, no solo a los de esta página. Esto puede sobreescribir el orden configurado.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#FF9F43",
                    cancelButtonColor: "#02913f",
                    confirmButtonText: "Sí, guardar",
                    cancelButtonText: "Cancelar"
                });
                if (!result.isConfirmed) return;
            }
            this.executeRelevanceSave();
        },
        async executeRelevanceSave() {
            this.$loading.show();
            const offset = (this.currentPage - 1) * this.listPageSize;
            try {
                await this.updateProductsWeights({
                    products: this.itemsCurrent.map((p, index) => ({
                        id: p.id,
                        weight: offset + index + 1
                    })),
                    reorder: this.reorderAll || undefined
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Relevancia actualizada",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onAdvancedOption(key) {
            if (key === "edit-order") this.toggleRelevanceMode();
            if (key === "pdf") this.downloadPDFComponent();
            if (key === "download") this.downLoadProducts();
        },
        buildMassiveDownloadFilters() {
            const { active, priceOffer, stock } = this.query;
            return {
                ...(active !== undefined ? { active } : null),
                ...(stock ? { stock } : null),
                ...(priceOffer ? { priceOffer } : null)
            };
        },
        describeRange({ end, start }, money = false) {
            const format = (value) =>
                money ? this.$options.filters.money(value) : value;
            if (start !== undefined && end !== undefined) {
                return `entre ${format(start)} y ${format(end)}`;
            }
            if (start !== undefined) return `desde ${format(start)}`;
            return `hasta ${format(end)}`;
        },
        describeMassiveDownloadFilters(filters) {
            const lines = [];
            if (filters.active !== undefined) {
                lines.push(
                    `🟡 Publicados: ${
                        filters.active ? "solo activos" : "solo inactivos"
                    }`
                );
            }
            if (filters.stock) {
                lines.push(`📦 Stock: ${this.describeRange(filters.stock)}`);
            }
            if (filters.priceOffer) {
                lines.push(
                    `💰 Precio de venta: ${this.describeRange(
                        filters.priceOffer,
                        true
                    )}`
                );
            }
            if (!lines.length) {
                return "Sin filtros: se incluirán <strong>todas</strong> las referencias.";
            }
            return `Con los filtros de la búsqueda actual:<br/>${lines.join(
                "<br/>"
            )}`;
        },
        async downLoadProducts() {
            const filters = this.buildMassiveDownloadFilters();
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Descargar todos los productos",
                html: `Se descargará un Excel con las referencias de tus productos.<br/><br/>${this.describeMassiveDownloadFilters(
                    filters
                )}`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Sí, descargar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#ee3f59",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) return;

            this.isLoadingDownload = true;
            this.$loading.show();
            try {
                const { items } = await this.listMassiveProducts(filters);
                downloadFileMassiveProducts("products", items);
                this.$sound.play();
                this.isLoadingDownload = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoadingDownload = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onRowAction({ action, item }) {
            switch (action) {
                case "open":
                    window.open(`/app/products/detail/${item.id}`, "_blank");
                    break;
                case "sales":
                    window.open(`/app/orders/product/${item.id}`, "_blank");
                    break;
                case "clone":
                    window.open(`/app/products/clone/${item.id}`, "_blank");
                    break;
                case "toggle-active":
                    this.activeMassiveProductsComponent();
                    break;
                case "preview":
                    this.openPreview();
                    break;
            }
        },
        async downloadPDFComponent(useSelected = false) {
            this.isOpenPreview = false;
            const items = useSelected ? this.selected : this.itemsCurrent;

            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Tipo de reporte",
                text: `¿Qué tipo de reporte ${
                    useSelected ? "para elementos seleccionados" : ""
                } deseas generar?`,
                icon: "question",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "👀 Reporte Parcial",
                denyButtonText: "📄 Reporte Completo",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#3085d6",
                denyButtonColor: "#28a745",
                cancelButtonColor: "#d33",
                allowOutsideClick: false
            });

            if (dialogResult.isDismissed) {
                return;
            }

            this.isGeneratingPDF = true;
            try {
                if (dialogResult.isConfirmed) {
                    await generateProductsListPDF(items);
                } else if (dialogResult.isDenied) {
                    await generateProductsListCompletePDF(items);
                }
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Error al generar PDF",
                    text: error.message || "No se pudo generar el archivo PDF",
                    icon: "error"
                });
            } finally {
                this.isGeneratingPDF = false;
            }
        }
    }
};
</script>
